'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AppLinkButton } from '@/components/mui/app-link-button';
import { useAppColors } from '@/styles/colors';
import { buildIdeasCatalogUrl, StrategyCard, type StrategyPreviewState } from './StrategyCard';
import { CATALOG_DEFAULT_SEARCHES, type CatalogDefaultSearch } from '@/libs/catalogo-default-searches';
import type { InstrumentRow } from '@/components/catalogo/types';

const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});
const DECIMAL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const TRAIL_CHIP_SX = {
  height: 32,
  borderRadius: 999,
  fontSize: '0.78rem',
  fontWeight: 800,
  px: 0.8,
  borderWidth: '1.5px',
};

const PRIMARY_ACTION_BUTTON_SX = {
  textTransform: 'none',
  fontWeight: 800,
  px: 1.8,
  minHeight: 38,
  borderRadius: 2,
};

const sortForPreview = (rows: InstrumentRow[]): InstrumentRow[] => {
  return [...rows].sort((a, b) => {
    const aDy = a.dy12mPct ?? Number.NEGATIVE_INFINITY;
    const bDy = b.dy12mPct ?? Number.NEGATIVE_INFINITY;
    if (aDy !== bDy) return bDy - aDy;
    return (a.idCem ?? '').localeCompare(b.idCem ?? '');
  });
};

const CATEGORY_META: Record<CatalogDefaultSearch['category'], { title: string; subtitle: string }> = {
  valor: {
    title: 'Valor e desconto',
    subtitle: 'Estratégias para buscar preço atrativo com filtros mínimos de qualidade.',
  },
  renda: {
    title: 'Renda e estabilidade',
    subtitle: 'Estratégias para foco em previsibilidade de caixa, liquidez e base de cotistas.',
  },
  recuperacao: {
    title: 'Recuperação e turnaround',
    subtitle: 'Estratégias para quem aceita volatilidade em busca de melhora operacional.',
  },
  risco: {
    title: 'Risco e proteção',
    subtitle: 'Estratégias para reduzir exposição a alavancagem, crédito e juros.',
  },
};

const CATEGORY_ORDER: CatalogDefaultSearch['category'][] = ['renda', 'valor', 'risco'];
const BEGINNER_ONLY_STRATEGY_IDS = new Set(['fiis-base-10']);
type ActiveTrail = 'all' | CatalogDefaultSearch['category'] | 'iniciante';
const TRAIL_QUERY_KEY = 'trilha';
const CATEGORY_QUERY_VALUE: Record<CatalogDefaultSearch['category'] | 'iniciante', string> = {
  iniciante: 'iniciantes',
  renda: 'renda',
  valor: 'valor',
  recuperacao: 'recuperacao',
  risco: 'risco',
};
const CATEGORY_BY_QUERY_VALUE: Record<string, ActiveTrail> = {
  iniciantes: 'iniciante',
  renda: 'renda',
  valor: 'valor',
  recuperacao: 'recuperacao',
  risco: 'risco',
};

type RangeFormatType = 'percent' | 'integer' | 'currency' | 'decimal';

const RANGE_FILTERS: Array<{
  minKey: string;
  maxKey: string;
  label: string;
  format: RangeFormatType;
}> = [
  { minKey: 'currentPriceMin', maxKey: 'currentPriceMax', label: 'Preço da cota', format: 'currency' },
  { minKey: 'currentPvpMin', maxKey: 'currentPvpMax', label: 'P/VP atual', format: 'decimal' },
  { minKey: 'dy12mMin', maxKey: 'dy12mMax', label: 'DY 12m', format: 'percent' },
  { minKey: 'cotistasMin', maxKey: 'cotistasMax', label: 'Número de cotistas', format: 'integer' },
  {
    minKey: 'liquidezDiaria30dMin',
    maxKey: 'liquidezDiaria30dMax',
    label: 'Liquidez diária média (30d)',
    format: 'currency',
  },
  {
    minKey: 'concentracaoTijoloMin',
    maxKey: 'concentracaoTijoloMax',
    label: 'Concentração em tijolo',
    format: 'percent',
  },
  { minKey: 'vacanciaMin', maxKey: 'vacanciaMax', label: 'Vacância', format: 'percent' },
  { minKey: 'inadimplenciaMin', maxKey: 'inadimplenciaMax', label: 'Inadimplência', format: 'percent' },
  {
    minKey: 'distanceTo12mHighMin',
    maxKey: 'distanceTo12mHighMax',
    label: 'Distância da máxima (12m)',
    format: 'percent',
  },
  {
    minKey: 'selicSensitivityScoreMin',
    maxKey: 'selicSensitivityScoreMax',
    label: 'Sensibilidade à Selic',
    format: 'percent',
  },
  {
    minKey: 'leverageIndexScoreMin',
    maxKey: 'leverageIndexScoreMax',
    label: 'Índice de alavancagem',
    format: 'percent',
  },
];

const SINGLE_FILTER_LABELS: Record<string, string> = {
  fundTypes: 'Tipo de fundo',
  classificacao: 'Classificação',
  segmento: 'Segmento',
  cashTrendStatus: 'Resultado de caixa',
  sort: 'Ordenação',
};
const FILTER_VALUE_LABELS: Record<string, Record<string, string>> = {
  fundTypes: {
    FII: 'FII',
    FIAGRO: 'FIAGRO',
    ETF: 'ETF',
  },
  classificacao: {
    tijolo: 'Tijolo',
    papel: 'Papel',
    hibrido: 'Híbrido',
    sem_dados: 'Sem dados',
  },
  cashTrendStatus: {
    POSITIVO: 'Positivo',
    NEGATIVO_COM_MELHORA: 'Negativo com melhora',
    NEGATIVO_SEM_MELHORA: 'Negativo sem melhora',
  },
  sort: {
    'dy12mPct:desc': 'DY 12m (maior para menor)',
    'dy12mPct:asc': 'DY 12m (menor para maior)',
  },
};

const humanizeFilterValue = (key: string, rawValue: string) => {
  const byKey = FILTER_VALUE_LABELS[key];
  if (!byKey) return rawValue;
  if (key === 'fundTypes') {
    return rawValue
      .split(',')
      .map((item) => byKey[item] ?? item)
      .join(', ');
  }
  return byKey[rawValue] ?? rawValue;
};

const STRATEGY_AVOID_WHEN: Record<string, string> = {
  'fiis-recuperacao': 'Evite se você não tolera volatilidade e mudanças bruscas de preço.',
  'fiis-alta-sensibilidade-selic': 'Evite se você quer baixa oscilação em cenários de juros incertos.',
  'fiis-base-10': 'Evite se o foco principal for crescimento acelerado de patrimônio no curto prazo.',
};

const defaultAvoidWhenByCategory: Record<CatalogDefaultSearch['category'], string> = {
  renda: 'Evite se você busca ganhos rápidos e aceita risco alto de oscilação.',
  valor: 'Evite se você não quer esperar convergência de preço no médio/longo prazo.',
  recuperacao: 'Evite se você prioriza previsibilidade e baixa volatilidade.',
  risco: 'Evite se você não quer acompanhar cenários macroeconômicos com frequência.',
};

const getAvoidWhen = (strategy: CatalogDefaultSearch) =>
  STRATEGY_AVOID_WHEN[strategy.id] ?? defaultAvoidWhenByCategory[strategy.category];

const parseNumericValue = (rawValue: string): number | null => {
  const value = rawValue.trim();
  if (!value) return null;
  if (value.includes(',')) {
    const parsed = Number(value.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (/^\d{1,3}(\.\d{3})+$/.test(value)) {
    const parsed = Number(value.replace(/\./g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatRangeValue = (rawValue: string, format: RangeFormatType): string => {
  const parsed = parseNumericValue(rawValue);
  if (parsed === null) return rawValue;

  if (format === 'currency') return CURRENCY_FORMATTER.format(parsed);
  if (format === 'integer') return INTEGER_FORMATTER.format(parsed);
  if (format === 'percent') return `${DECIMAL_FORMATTER.format(parsed)}%`;
  return DECIMAL_FORMATTER.format(parsed);
};

const joinHumanList = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} e ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;
};

const parseCriteria = (queryString: string) => {
  const params = new URLSearchParams(queryString);
  const criteriaLines: string[] = [];
  const consumedKeys = new Set<string>();

  RANGE_FILTERS.forEach((range) => {
    const min = params.get(range.minKey);
    const max = params.get(range.maxKey);
    if (!min && !max) return;

    consumedKeys.add(range.minKey);
    consumedKeys.add(range.maxKey);

    const minLabel = min ? formatRangeValue(min, range.format) : null;
    const maxLabel = max ? formatRangeValue(max, range.format) : null;

    if (minLabel && maxLabel) {
      criteriaLines.push(`${range.label} entre ${minLabel} e ${maxLabel}.`);
      return;
    }
    if (minLabel) {
      criteriaLines.push(`${range.label} a partir de ${minLabel}.`);
      return;
    }
    if (maxLabel) {
      criteriaLines.push(`${range.label} até ${maxLabel}.`);
    }
  });

  Object.entries(SINGLE_FILTER_LABELS).forEach(([key, label]) => {
    const value = params.get(key);
    if (!value) return;
    consumedKeys.add(key);

    if (key === 'fundTypes') {
      const types = humanizeFilterValue(key, value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      criteriaLines.unshift(`${label}: ${joinHumanList(types)}.`);
      return;
    }

    criteriaLines.unshift(`${label}: ${humanizeFilterValue(key, value)}.`);
  });

  params.forEach((value, key) => {
    if (consumedKeys.has(key)) return;
    criteriaLines.push(`${key}: ${value}.`);
  });

  return criteriaLines;
};

export function IdeasByObjectiveClient() {
  const appColors = useAppColors();
  const router = useRouter();
  const pathname = usePathname();
  const safePathname = pathname ?? '/ideias-para-investir-por-objetivo';
  const searchParams = useSearchParams();
  const [previewById, setPreviewById] = useState<Record<string, StrategyPreviewState>>({});
  const [activeCategory, setActiveCategory] = useState<ActiveTrail>('all');
  const [criteriaDialog, setCriteriaDialog] = useState<CatalogDefaultSearch | null>(null);

  useEffect(() => {
    if (!searchParams) return;
    const raw = (searchParams?.get(TRAIL_QUERY_KEY) ?? '').toLowerCase();
    const parsed = CATEGORY_BY_QUERY_VALUE[raw];
    setActiveCategory(parsed ?? 'all');
  }, [searchParams]);

  useEffect(() => {
    if (!searchParams) return;
    const params = new URLSearchParams(searchParams?.toString() ?? '');
    if (activeCategory === 'all') {
      params.delete(TRAIL_QUERY_KEY);
    } else {
      params.set(TRAIL_QUERY_KEY, CATEGORY_QUERY_VALUE[activeCategory]);
    }
    const nextQuery = params.toString();
    const currentQuery = searchParams?.toString() ?? '';
    if (nextQuery === currentQuery) return;
    router.replace(nextQuery ? `${safePathname}?${nextQuery}` : safePathname, { scroll: false });
  }, [activeCategory, router, safePathname, searchParams]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setPreviewById(
      Object.fromEntries(
        CATALOG_DEFAULT_SEARCHES.map((strategy) => [
          strategy.id,
          { loading: true, error: null, rows: [], total: 0 } as StrategyPreviewState,
        ]),
      ),
    );

    const load = async () => {
      await Promise.all(
        CATALOG_DEFAULT_SEARCHES.map(async (strategy) => {
          try {
            const query = strategy.queryString ? `?${strategy.queryString}` : '';
            const res = await fetch(`/api/catalogo/tickers${query}`, {
              signal: controller.signal,
              cache: 'no-store',
            });
            if (!res.ok) {
              throw new Error(`Erro ao carregar prévia (${res.status})`);
            }
            const rows = (await res.json()) as InstrumentRow[];
            if (cancelled) return;
            const preview = sortForPreview(rows).slice(0, 5);
            setPreviewById((current) => ({
              ...current,
              [strategy.id]: {
                loading: false,
                error: null,
                rows: preview,
                total: rows.length,
              },
            }));
          } catch (err: any) {
            if (cancelled || err?.name === 'AbortError') return;
            setPreviewById((current) => ({
              ...current,
              [strategy.id]: {
                loading: false,
                error: err?.message ?? 'Falha ao carregar prévia',
                rows: [],
                total: 0,
              },
            }));
          }
        }),
      );
    };

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const groupedStrategies = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        category,
        ...CATEGORY_META[category],
        strategies: CATALOG_DEFAULT_SEARCHES.filter(
          (strategy) => strategy.category === category && !BEGINNER_ONLY_STRATEGY_IDS.has(strategy.id),
        ),
      })).filter((section) => section.strategies.length > 0),
    [],
  );
  const visibleGroups = useMemo(
    () =>
      activeCategory === 'all'
        ? groupedStrategies
        : activeCategory === 'iniciante'
          ? []
          : groupedStrategies.filter((group) => group.category === activeCategory),
    [activeCategory, groupedStrategies],
  );
  const starterStrategies = useMemo(
    () =>
      ['fiis-base-10', 'fiis-defensivos-renda', 'desagio-pvp-menor-1']
        .map((id) => CATALOG_DEFAULT_SEARCHES.find((strategy) => strategy.id === id))
        .filter((strategy): strategy is CatalogDefaultSearch => Boolean(strategy)),
    [],
  );

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1.5, md: 2 } }}>
      <Paper
        variant='outlined'
        sx={{
          p: { xs: 1.5, md: 2.25 },
          borderRadius: 2.5,
          backgroundColor: 'hsl(var(--card))',
          borderColor: 'hsl(var(--border))',
          maxWidth: 1780,
          mx: 'auto',
        }}
      >
        <Stack spacing={0.6} sx={{ mb: 2 }}>
          <Typography variant='h4' fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.05rem' } }}>
            Ideias para investir por objetivo
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 900 }}>
            Escolha uma estratégia pronta, entenda o racional em linguagem simples e abra o catálogo já com os filtros
            aplicados.
          </Typography>
          <Stack direction='row' spacing={0.8} flexWrap='wrap' useFlexGap sx={{ pt: 0.4 }}>
            <Chip
              size='small'
              label='Todas'
              variant={activeCategory === 'all' ? 'filled' : 'outlined'}
              onClick={() => setActiveCategory('all')}
              sx={{
                ...TRAIL_CHIP_SX,
                ...(activeCategory === 'all'
                  ? {
                      backgroundColor: appColors.semantic.warning,
                      borderColor: appColors.semantic.warning,
                      color: appColors.actions.range.selectedText,
                      '&:hover': { backgroundColor: appColors.semantic.warningStrong },
                    }
                  : {
                      color: appColors.actions.range.unselectedText,
                      borderColor: appColors.actions.range.unselectedBorder,
                      '&:hover': {
                        color: appColors.actions.range.unselectedHoverText,
                        borderColor: appColors.actions.range.unselectedHoverBorder,
                        backgroundColor: appColors.actions.range.unselectedHoverBg,
                      },
                    }),
              }}
            />
            <Chip
              size='small'
              label='Bons para iniciantes'
              variant={activeCategory === 'iniciante' ? 'filled' : 'outlined'}
              onClick={() => setActiveCategory('iniciante')}
              sx={{
                ...TRAIL_CHIP_SX,
                ...(activeCategory === 'iniciante'
                  ? {
                      backgroundColor: appColors.semantic.warning,
                      borderColor: appColors.semantic.warning,
                      color: appColors.actions.range.selectedText,
                      '&:hover': { backgroundColor: appColors.semantic.warningStrong },
                    }
                  : {
                      color: appColors.actions.range.unselectedText,
                      borderColor: appColors.actions.range.unselectedBorder,
                      '&:hover': {
                        color: appColors.actions.range.unselectedHoverText,
                        borderColor: appColors.actions.range.unselectedHoverBorder,
                        backgroundColor: appColors.actions.range.unselectedHoverBg,
                      },
                    }),
              }}
            />
            {CATEGORY_ORDER.map((category) => (
              <Chip
                key={category}
                size='small'
                label={CATEGORY_META[category].title}
                variant={activeCategory === category ? 'filled' : 'outlined'}
                onClick={() => setActiveCategory(category)}
                sx={{
                  ...TRAIL_CHIP_SX,
                  ...(activeCategory === category
                    ? {
                        backgroundColor: appColors.semantic.warning,
                        borderColor: appColors.semantic.warning,
                        color: appColors.actions.range.selectedText,
                        '&:hover': { backgroundColor: appColors.semantic.warningStrong },
                      }
                    : {
                        color: appColors.actions.range.unselectedText,
                        borderColor: appColors.actions.range.unselectedBorder,
                        '&:hover': {
                          color: appColors.actions.range.unselectedHoverText,
                          borderColor: appColors.actions.range.unselectedHoverBorder,
                          backgroundColor: appColors.actions.range.unselectedHoverBg,
                        },
                      }),
                }}
              />
            ))}
          </Stack>
        </Stack>
        {activeCategory === 'all' || activeCategory === 'iniciante' ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Stack spacing={0.2}>
              <Typography variant='h6' fontWeight={800} sx={{ fontSize: { xs: '1.02rem', md: '1.12rem' } }}>
                Bons para iniciantes
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Estratégias simples para quem está começando e quer comparar os primeiros FIIs.
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gap: 1.4,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                },
              }}
            >
              {starterStrategies.map((strategy) => {
                const state = previewById[strategy.id] ?? {
                  loading: true,
                  error: null,
                  rows: [],
                  total: 0,
                };
                return (
                  <StrategyCard
                    key={strategy.id}
                    strategy={strategy}
                    state={state}
                    avoidWhen={getAvoidWhen(strategy)}
                    onOpenCriteria={setCriteriaDialog}
                  />
                );
              })}
            </Box>
          </Stack>
        ) : null}

        <Stack spacing={2.2}>
          {visibleGroups.map((section) => (
            <Stack key={section.category} spacing={1.1}>
              <Stack spacing={0.2}>
                <Typography variant='h6' fontWeight={800} sx={{ fontSize: { xs: '1.02rem', md: '1.12rem' } }}>
                  {section.title}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {section.subtitle}
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.4,
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                }}
              >
                {section.strategies.map((strategy) => {
                  const state = previewById[strategy.id] ?? {
                    loading: true,
                    error: null,
                    rows: [],
                    total: 0,
                  };
                  return (
                    <StrategyCard
                      key={strategy.id}
                      strategy={strategy}
                      state={state}
                      avoidWhen={getAvoidWhen(strategy)}
                      onOpenCriteria={setCriteriaDialog}
                    />
                  );
                })}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Paper>
      <Dialog open={Boolean(criteriaDialog)} onClose={() => setCriteriaDialog(null)} maxWidth='sm' fullWidth>
        <DialogTitle>{criteriaDialog?.name ?? 'Critérios da estratégia'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <Typography variant='body2' color='text.secondary'>
              Esta estratégia aplica os seguintes critérios:
            </Typography>
            {(criteriaDialog ? parseCriteria(criteriaDialog.queryString) : []).map((line) => (
              <Typography key={line} variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                • {line}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCriteriaDialog(null)}
            sx={{
              color: appColors.actions.accentOutline.text,
              '&:hover': {
                color: appColors.actions.accentOutline.hoverText,
                backgroundColor: appColors.actions.accentOutline.hoverBg,
              },
            }}
          >
            Fechar
          </Button>
          {criteriaDialog ? (
            <AppLinkButton
              href={buildIdeasCatalogUrl(criteriaDialog.catalogPath, criteriaDialog.queryString, criteriaDialog.id)}
              variant='contained'
              color='warning'
              size='small'
              sx={{
                ...PRIMARY_ACTION_BUTTON_SX,
              }}
            >
              Abrir estratégia
            </AppLinkButton>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
